import {
  isSome,
  Maybe,
  useAsyncAction,
  v2_ProductList,
} from '@crocodille/common';
import { useIntersectionObserver } from '@hooks/use-intersection-observer';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { MouseEvent, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as routing from '../../../routing';
import breakpointsFile from '../../../styles/breakpoints.json';
import Button from '../../elements/button/button';
import Image from '../../elements/image/image';
import CartAddIcon from '../../icons/cart-add-icon';
import SpinnerIcon from '../../icons/spinner-icon';
import ProductOnlyDelivery from '../product-only-delivery/product-only-delivery';
import ProductOnlyPickup from '../product-only-pickup/product-only-pickup';
import ProductOutOfStock from '../product-out-of-stock/product-out-of-stock';
import ProductSpecialOffer from '../product-special-offer/product-special-offer';
import ProductTags from '../product-tags/product-tags';
import cn from 'classnames';
import styles from './product-card.module.scss';

const ProductCardPrice = dynamic(() => import('./product-card-price'), {
  ssr: false,
  loading: () => <span className={cn(styles.price)}></span>,
});

/**
 * ! Breakpoints are extracted here due to issue with named imports from json files.
 */
const BREAKPOINTS = breakpointsFile.breakpoints;

const PRODUCT_IMAGE_SIZES = [
  `${BREAKPOINTS['product-list-columns-3']} 33vw`,
  `${BREAKPOINTS['product-list-columns-2']} 50vw`,
  '100vw',
].join(', ');

export interface ProductCardProps {
  readonly id: number;
  readonly title: string;
  readonly price?: Maybe<number>;
  readonly description?: Maybe<string>;
  readonly image?: Maybe<string>;
  readonly isOnlyPickup?: Maybe<boolean>;
  readonly isOnlyDelivery?: Maybe<boolean>;
  readonly isSpecialItem?: Maybe<boolean>;
  readonly isAvailable?: Maybe<boolean>;
  readonly slug: string;
  readonly tags?: string[];
  onAddToCart(): Promise<void>;
  readonly bigCardItems?: boolean;
  readonly taxon: Omit<v2_ProductList[number], 'products'>;
}

/**
 * A card displaying product information in product listing.
 */
const ProductCard: React.FC<React.PropsWithChildren<ProductCardProps>> = ({
  id,
  title,
  description,
  image,
  price,
  isOnlyPickup,
  isOnlyDelivery,
  isSpecialItem = false,
  isAvailable = true,
  slug,
  onAddToCart,
  tags = [],
  bigCardItems = true,
  taxon,
}) => {
  isOnlyDelivery = isOnlyDelivery ?? false;
  isOnlyPickup = isOnlyPickup ?? false;

  const { t } = useTranslation();
  const { query } = useRouter();

  const handleAddToCartCb = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      await onAddToCart();
    },
    [onAddToCart],
  );

  const { pending: addingToCart, action: handleAddToCart } = useAsyncAction(
    handleAddToCartCb,
    [],
  );

  const containerRef = useRef<HTMLLIElement>(null);

  useIntersectionObserver(containerRef, {
    type: 'product-card',
    id,
  });

  return (
    <li
      ref={containerRef}
      className={cn(styles.container, { [styles.isBig]: bigCardItems })}
    >
      <Link
        href={routing.products({
          taxon: Array.isArray(query.taxon)
            ? query.taxon[0]
            : query.taxon || taxon.slug,
          product: slug,
        })}
        prefetch={false}
        scroll={false}
        className={styles.card}
      >
        <div className={styles.preview}>
          <Image
            className={styles.image}
            src={image}
            alt={title}
            layout="fill"
            sizes={PRODUCT_IMAGE_SIZES}
          />
          <ProductTags
            tags={tags}
            inset
            className={cn(styles.tag, { [styles.isLargeTag]: bigCardItems })}
          />
          <ProductOnlyPickup isOnlyPickup={isOnlyPickup} />
          <ProductOnlyDelivery isOnlyDelivery={isOnlyDelivery} />
          <ProductSpecialOffer isSpecialItem={isSpecialItem} />
          <ProductOutOfStock isAvailable={isAvailable} />
        </div>
        <div className={styles.details}>
          <h3 className={styles.title}>{title}</h3>
          {isSome(description) ? (
            <p className={styles.description}>{description}</p>
          ) : (
            ''
          )}

          <div className={styles.controls}>
            <ProductCardPrice price={price} />
            <Button
              className={cn(styles.addToCart, {
                [styles.round]: !bigCardItems,
              })}
              size="medium"
              symmetrical={false}
              disabled={!isAvailable || isOnlyDelivery || isOnlyPickup}
              onClick={handleAddToCart}
            >
              <span className={styles.buttonText}>{t('cart.add_to_cart')}</span>
              <Button.Icon>
                {addingToCart ? (
                  <SpinnerIcon className={styles.addToCartIcon} />
                ) : (
                  <CartAddIcon className={styles.addToCartIcon} />
                )}
              </Button.Icon>
            </Button>
          </div>
        </div>
      </Link>
    </li>
  );
};

export default ProductCard;
